import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import RegressionSuiteCard from "./RegressionSuiteCard";
import AddTestcaseDrawer from "../Drawer/AddTestCaseDrawer";
import {
  deleteRegressionPlan_API,
  getRegressionPlan_API,
} from "../../../api/controller/RegressionController";
import { useApplicationContext } from "../../../context/ApplicationContext";
import { Severity } from "../../../constant/ApplicationVariables";
import ExecuteModal from "../../scriptEdtiorNew/editor/ExecuteModal";

const SuiteList = ({ suites = [], onEdit, onOpenSuite, onSuiteDeleted }) => {
  const [suiteList, setSuiteList] = useState([]);
  const { activeProject, handleSnackbar } = useApplicationContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [linkedTestCases, setLinkedTestCases] = useState([]);

  // Execution modal
  const [executionModalOpen, setExecutionModalOpen] = useState(false);
  const [suiteToExecute, setSuiteToExecute] = useState(null);

  useEffect(() => {
    setSuiteList(suites);
  }, [suites]);

  const handleCardClick = async (suite) => {
    setSelectedSuite(suite);
    try {
      const response = await getRegressionPlan_API({
        name: suite.name,
        projectName: activeProject?.name,
      });
      setLinkedTestCases(response.data.testCases || []);
    } catch (err) {
      console.error(err);
      handleSnackbar("Failed to load linked test cases", "error");
      setLinkedTestCases([]);
    }
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedSuite(null);
    setLinkedTestCases([]);
  };

  const handleDelete = async (suite) => {
    try {
      await deleteRegressionPlan_API({
        name: suite.name,
        projectName: activeProject?.name,
      });
      handleSnackbar("Regression plan deleted successfully", Severity.success);
      setSuiteList((prev) => prev.filter((s) => s.name !== suite.name));
      onSuiteDeleted?.(suite.name);
    } catch (error) {
      console.error("Delete failed", error);
      handleSnackbar("Failed to delete regression plan", Severity.error);
    }
  };

  const handleExecuteClick = (suite) => {
    console.log("Executing suite:", suite); // Debug log
    setSuiteToExecute(suite);
    setExecutionModalOpen(true);
  };
  
  const handleRemoveTestCaseFromDrawer = (tcId) => {
    setLinkedTestCases((prev) => prev.filter((tc) => tc.customId !== tcId));
  };

  return (
    <>
      {/* Grid of Regression Suites */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
            xl: "repeat(5, 1fr)",
          },
          gap: 2,
          maxHeight: "calc(100vh - 150px)",
          overflowY: "auto",
          p: 2,
        }}
      >
        {suiteList.map((suite) => (
          <RegressionSuiteCard
            key={suite.name}
            suite={suite}
            onClick={handleCardClick}
            onDelete={handleDelete}
            onEdit={(suite) => onEdit(suite)}
            onExecute={handleExecuteClick}
          />
        ))}
      </Box>

      {/* Drawer for linked test cases */}
      <AddTestcaseDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        suite={selectedSuite}
        testCases={linkedTestCases}
        onAddTestCases={(updatedTestCases) => {
          onOpenSuite(selectedSuite, updatedTestCases);
          setLinkedTestCases(updatedTestCases);
        }}
        onEdit={(currentTestCases) =>
          onOpenSuite(selectedSuite, currentTestCases)
        }
        onRemoveTestCase={(tcId) => {
          setLinkedTestCases((prev) =>
            prev.filter((tc) => tc.customId !== tcId),
          );
        }}
      />

      {/* Execution Modal - FIXED */}
      {executionModalOpen && suiteToExecute && (
        <ExecuteModal
          open={executionModalOpen}
          suite={suiteToExecute}  // Use suiteToExecute, not selectedSuite
          planName={suiteToExecute?.name}  // Pass the plan name explicitly
          executionType="REGRESSION"
          onClose={() => {
            setExecutionModalOpen(false);
            setSuiteToExecute(null);
          }}
          activeProject={activeProject}  // Pass the active project if needed
        />
      )}
    </>
  );
};

export default SuiteList;